import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  ServiceCreateRequest,
  ServiceItem,
  ServiceUpdateRequest,
} from '@crm/shared';
import { api } from '../../lib/api';

export type ServiceListFilter = {
  active: 'true' | 'false' | 'all';
  category?: string;
  search?: string;
};

export const servicesKeys = {
  all: ['services'] as const,
  list: (f: ServiceListFilter) => ['services', 'list', f] as const,
  one: (id: number) => ['services', 'one', id] as const,
};

export function useServicesList(filter: ServiceListFilter) {
  return useQuery({
    queryKey: servicesKeys.list(filter),
    queryFn: async () => {
      const params: Record<string, string> = { active: filter.active };
      if (filter.category) params.category = filter.category;
      if (filter.search) params.search = filter.search;
      const res = await api.get<ServiceItem[]>('/services', { params });
      return res.data;
    },
  });
}

export function useServiceCreate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: ServiceCreateRequest) => {
      const res = await api.post<ServiceItem>('/services', input);
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: servicesKeys.all }),
  });
}

export function useServiceUpdate(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: ServiceUpdateRequest) => {
      const res = await api.patch<ServiceItem>(`/services/${id}`, input);
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: servicesKeys.all }),
  });
}

export function useServiceDeactivate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      const res = await api.delete<ServiceItem>(`/services/${id}`);
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: servicesKeys.all }),
  });
}

export function useServiceActivate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      const res = await api.post<ServiceItem>(`/services/${id}/activate`);
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: servicesKeys.all }),
  });
}
